import React, { useEffect, useMemo, useState } from 'react';
import { driveFolders, driveMove, drivePath, isDriveFolder } from './driveApi';
import './AssetsExplorer.css';

const MoveModal = ({
  open,
  onClose,
  item,
  firebase,
  rootFolderId,
  currentFolderId,
  rootLabel,
  onMoved,
}) => {
  const [destinationFolderId, setDestinationFolderId] = useState(currentFolderId || rootFolderId);
  const [destinationBreadcrumb, setDestinationBreadcrumb] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canGoBack = destinationBreadcrumb.length > 1;

  const loadDestination = async (folderId) => {
    if (!firebase || !rootFolderId || !folderId) return;
    setLoading(true);
    setError('');
    try {
      const [folderResp, pathResp] = await Promise.all([
        driveFolders(firebase, folderId),
        drivePath(firebase, folderId),
      ]);
      setFolders(folderResp.folders || []);
      setDestinationBreadcrumb(pathResp.path || []);
    } catch (err) {
      console.error('Failed to load destination folders:', err);
      setError("Can't load folders. Try again.");
      setFolders([]);
      setDestinationBreadcrumb([{ id: rootFolderId, name: rootLabel }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const initial = currentFolderId || rootFolderId;
    setDestinationFolderId(initial);
    loadDestination(initial);
  }, [open, currentFolderId, rootFolderId]);

  const title = useMemo(() => {
    const name = item?.name || 'this item';
    return `Move "${name}" to...`;
  }, [item]);

  const handleMove = async (targetId) => {
    if (!item?.id || !targetId) return;
    setError('');
    try {
      await driveMove(firebase, item.id, targetId);
      onClose();
      if (onMoved) {
        await onMoved();
      }
    } catch (err) {
      console.error('Move failed:', err);
      setError("Can't move there.");
    }
  };

  if (!open || !item) return null;

  return (
    <div role="dialog" aria-modal="true" className="studio-modal-overlay">
      <div className="studio-modal">
        <div className="studio-modal-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeWidth="1.5" d="M4 12h16M12 4l4 4-4 4" />
          </svg>
        </div>
        <h3 className="studio-modal-title">{title}</h3>
        <p className="studio-modal-subtitle">Choose a destination folder.</p>

        <nav aria-label="Breadcrumb" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {destinationBreadcrumb.map((crumb, index) => {
            const isLast = index === destinationBreadcrumb.length - 1;
            return (
              <button
                key={crumb.id}
                type="button"
                className="nav-button"
                onClick={() => {
                  if (isLast) return;
                  setDestinationFolderId(crumb.id);
                  loadDestination(crumb.id);
                }}
                disabled={isLast}
                style={{ padding: '0.3rem 0.55rem' }}
              >
                {crumb.name || 'Folder'}
              </button>
            );
          })}
        </nav>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
          <button
            type="button"
            className="studio-modal-button secondary"
            onClick={() => {
              if (!canGoBack) return;
              const parent = destinationBreadcrumb[destinationBreadcrumb.length - 2];
              if (!parent?.id) return;
              setDestinationFolderId(parent.id);
              loadDestination(parent.id);
            }}
            disabled={!canGoBack}
          >
            Back
          </button>
          {destinationFolderId !== rootFolderId ? (
            <button
              type="button"
              className="studio-modal-button secondary"
              onClick={() => handleMove(rootFolderId)}
            >
              Move to Root
            </button>
          ) : null}
        </div>

        <div style={{ minHeight: 140, border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10 }}>
          {loading ? (
            <p style={{ padding: '0.75rem' }}>Loading folders...</p>
          ) : folders.length ? (
            <div style={{ display: 'grid', gap: '0.35rem', padding: '0.6rem' }}>
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  type="button"
                  className="nav-button"
                  onClick={() => {
                    if (!isDriveFolder(folder)) return;
                    setDestinationFolderId(folder.id);
                    loadDestination(folder.id);
                  }}
                  style={{ textAlign: 'left' }}
                >
                  {folder.name || 'Untitled folder'}
                </button>
              ))}
            </div>
          ) : (
            <p style={{ padding: '0.75rem', opacity: 0.7 }}>No folders here.</p>
          )}
        </div>

        {error ? <p style={{ margin: 0 }}>{error}</p> : null}

        <div className="studio-modal-actions">
          <button type="button" className="studio-modal-button primary" onClick={() => handleMove(destinationFolderId)}>
            Move
          </button>
          <button type="button" className="studio-modal-button secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveModal;
